import { TIO_API_BASE_URL } from '../utils/constants';
import apiServiceInstance from './apiService';

/**
 * Tomorrow.io Timelines API Service
 * 
 * Provides direct access to Tomorrow.io's Timelines API.
 * Uses user authentication tokens (JWT) for API access.
 * 
 * Docs: https://docs.tomorrow.io/reference/post-timelines
 */

// Response types for the Timelines API
export interface TimelineInterval {
  startTime: string;
  values: Record<string, number | null>;
}

export interface Timeline {
  timestep: string;
  startTime: string;
  endTime: string;
  intervals: TimelineInterval[];
}

export interface TimelinesResponse {
  data: {
    timelines: Timeline[];
  };
}

export interface TimelinesRequestParams {
  location: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  fields: string[];
  timesteps: string[];
  startTime: string;
  endTime: string;
  units?: 'metric' | 'imperial';
  excludedLayers?: string[];
}

// Default fallback fields (used if no forecast fields are provided)
export const DEFAULT_FIELDS = [
  'temperature',
  'temperatureApparent',
  'humidity',
  'windSpeed',
  'windGust',
  'windDirection',
  'precipitationProbability',
  'precipitationIntensity',
  'rainIntensity',
  'pressureSurfaceLevel',
  'cloudCover',
  'visibility',
  'dewPoint',
  'uvIndex',
  'weatherCode',
];

// Cache for timeline data to avoid redundant API calls
const timelineCache = new Map<string, { data: TimelineInterval; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

/**
 * Determine the appropriate timestep based on timestamp
 * -7d to +6h: use 1m (1 minute) timestep
 * After +6h: use 1h (1 hour) timestep
 */
function getTimestepForTimestamp(timestamp: string): '1m' | '1h' {
  const now = new Date();
  const targetTime = new Date(timestamp);
  const diffMs = targetTime.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  
  // If timestamp is within 6 hours from now, use 1-minute timestep
  // Otherwise use 1-hour timestep
  return diffHours <= 6 ? '1m' : '1h';
}

/**
 * Generate a cache key for a specific location, time, fields, and timestep
 */
function getCacheKey(lat: number, lon: number, time: string, timestep: string, fields?: string[]): string {
  // Round coordinates to 4 decimal places for cache key
  // Include a hash of fields to ensure different field sets have different cache keys
  // Include timestep to ensure different timesteps don't collide
  const fieldsHash = fields ? fields.sort().join(',').substring(0, 50) : 'default';
  return `${lat.toFixed(4)},${lon.toFixed(4)},${time},${timestep},${fieldsHash}`;
}

/**
 * Fetch timeline data for a specific point and time range
 */
export async function fetchTimelinesData(
  latitude: number,
  longitude: number,
  startTime: string,
  endTime: string,
  timestep: '1m' | '1h',
  fields: string[] = DEFAULT_FIELDS
): Promise<TimelinesResponse> {
  const token = apiServiceInstance.getToken();
  
  if (!token) {
    throw new Error('No authentication token available');
  }

  const payload: TimelinesRequestParams = {
    location: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
    fields,
    timesteps: [timestep],
    startTime,
    endTime,
    units: 'metric',
    excludedLayers: ['FIA'],
  };

  const response = await fetch(`${TIO_API_BASE_URL}/timelines`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Timelines API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get timeline data for a specific timestamp (with caching)
 * Returns the interval data closest to the requested time
 * Automatically selects 1m timestep for -7d to +6h, 1h timestep for beyond +6h
 */
export async function getTimelineForTimestamp(
  latitude: number,
  longitude: number,
  timestamp: string,
  fields: string[] = DEFAULT_FIELDS
): Promise<TimelineInterval | null> {
  // Determine the appropriate timestep based on timestamp
  const timestep = getTimestepForTimestamp(timestamp);
  const cacheKey = getCacheKey(latitude, longitude, timestamp, timestep, fields);
  
  // Check cache first
  const cached = timelineCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  // Parse the timestamp and create an appropriate window around it
  const targetTime = new Date(timestamp);
  let startTime: Date;
  let endTime: Date;

  if (timestep === '1m') {
    // For 1-minute timestep, create a 5-minute window around the target time
    // This ensures we get the closest minute interval
    startTime = new Date(targetTime);
    startTime.setMinutes(startTime.getMinutes() - 2, 0, 0); // 2 minutes before
    endTime = new Date(targetTime);
    endTime.setMinutes(endTime.getMinutes() + 3, 0, 0); // 3 minutes after
  } else {
    // For 1-hour timestep, round to the hour
    startTime = new Date(targetTime);
    startTime.setMinutes(0, 0, 0); // Round down to hour
    endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 1);
  }

  try {
    const response = await fetchTimelinesData(
      latitude,
      longitude,
      startTime.toISOString(),
      endTime.toISOString(),
      timestep,
      fields
    );

    if (response.data.timelines.length > 0 && response.data.timelines[0].intervals.length > 0) {
      const intervals = response.data.timelines[0].intervals;
      
      // Find the interval closest to the requested timestamp
      const targetTimeMs = targetTime.getTime();
      let closestInterval = intervals[0];
      let minDiff = Math.abs(new Date(closestInterval.startTime).getTime() - targetTimeMs);

      for (const interval of intervals) {
        const diff = Math.abs(new Date(interval.startTime).getTime() - targetTimeMs);
        if (diff < minDiff) {
          minDiff = diff;
          closestInterval = interval;
        }
      }
      
      // Cache the result
      timelineCache.set(cacheKey, {
        data: closestInterval,
        timestamp: Date.now(),
      });
      
      return closestInterval;
    }
  } catch (error) {
    console.error('Failed to fetch timeline data:', error);
    throw error;
  }

  return null;
}

/**
 * Clear the timeline cache
 */
export function clearTimelineCache(): void {
  timelineCache.clear();
}

/**
 * Get cached data if available (without fetching)
 */
export function getCachedTimeline(
  latitude: number,
  longitude: number,
  timestamp: string,
  fields?: string[]
): TimelineInterval | null {
  const timestep = getTimestepForTimestamp(timestamp);
  const cacheKey = getCacheKey(latitude, longitude, timestamp, timestep, fields);
  const cached = timelineCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }
  
  return null;
}

const tomorrowIoTimelinesService = {
  fetchTimelinesData,
  getTimelineForTimestamp,
  clearTimelineCache,
  getCachedTimeline,
  DEFAULT_FIELDS,
};

export default tomorrowIoTimelinesService;
